import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ApplicationFormDetails } from '../../../../../../shared/models/application-form-details';
import { DateValidators } from '../../../../../../shared/validators/date-validators';
import { ApplicationFormEditFormModel } from './application-form-edit-form.model';
import {
  getMonthIndex,
  getMonthName,
} from '../../../../../../shared/constants/month-options';

export class ApplicationFormEditFormBuilder {
  static createFormGroup(
    application: ApplicationFormDetails,
  ): FormGroup<ApplicationFormEditFormModel> {
    return new FormGroup<ApplicationFormEditFormModel>(
      {
        id: new FormControl({ value: application.id, disabled: true }, [
          Validators.required,
        ]),
        code: new FormControl({ value: application.kod, disabled: true }, [
          Validators.required,
          Validators.maxLength(50),
        ]),
        name: new FormControl(application.nazwa, [
          Validators.required,
          Validators.maxLength(1024),
        ]),
        area: new FormControl({ value: application.grupa, disabled: true }, [
          Validators.required,
        ]),
        technicalCode: new FormControl(
          { value: application.kodTech, disabled: true },
          [Validators.required, Validators.maxLength(50)],
        ),
        deleted: new FormControl(application.usuniety, [Validators.required]),
        correspondencePossible: new FormControl(
          application.czyMozliwaKorespondencja,
        ),
        correspondenceEnabled: new FormControl(
          {
            value: application.czyKorespondencjaAktywna,
            disabled: !application.czyMozliwaKorespondencja,
          },
          [Validators.required],
        ),
        visibleForAreas: new FormControl(
          {
            value: application.wyswietlajDlaObszarow,
            disabled: !application.zmianaObszaru,
          },
          [Validators.required, Validators.maxLength(50)],
        ),
        visibleForAreasChange: new FormControl(application.zmianaObszaru),
        monthFrom: new FormControl(getMonthName(application.miesiacOd)),
        monthTo: new FormControl(getMonthName(application.miesiacDo)),
        dayFrom: new FormControl(application.dzienOd, [
          Validators.min(1),
          Validators.max(31),
        ]),
        dayTo: new FormControl(application.dzienDo, [
          Validators.min(1),
          Validators.max(31),
        ]),
      },
      {
        validators: [
          DateValidators.monthDayRange(
            'monthFrom',
            'monthTo',
            'dayFrom',
            'dayTo',
          ),
        ],
      },
    );
  }

  public static toModel(
    formGroup: FormGroup<ApplicationFormEditFormModel>,
    applicationId: number,
  ): ApplicationFormDetails {
    return {
      id: applicationId,
      kod: formGroup.controls.code.value ?? '',
      nazwa: formGroup.controls.name.value ?? '',
      grupa: formGroup.controls.area.value ?? null,
      kodTech: formGroup.controls.technicalCode.value ?? '',
      usuniety: !!formGroup.controls.deleted.value,
      czyMozliwaKorespondencja:
        !!formGroup.controls.correspondencePossible.value,
      czyKorespondencjaAktywna:
        !!formGroup.controls.correspondenceEnabled.value,
      wyswietlajDlaObszarow: formGroup.controls.visibleForAreas.value ?? null,
      zmianaObszaru: !!formGroup.controls.visibleForAreasChange.value,
      miesiacOd: getMonthIndex(formGroup.controls.monthFrom.value),
      miesiacDo: getMonthIndex(formGroup.controls.monthTo.value),
      dzienOd: formGroup.controls.dayFrom.value,
      dzienDo: formGroup.controls.dayTo.value,
    };
  }

  public static updateRangeValidators(
    formGroup: FormGroup<ApplicationFormEditFormModel>,
  ): void {
    if (!formGroup) {
      return;
    }

    const monthRequired =
      formGroup.controls.monthFrom.value !== null ||
      formGroup.controls.monthTo.value !== null ||
      formGroup.controls.dayFrom.value !== null ||
      formGroup.controls.dayTo.value !== null;

    const dayRequired =
      !!formGroup.controls.dayFrom.value ||
      !!formGroup.controls.dayTo.value ||
      formGroup.controls.monthFrom.value !== null ||
      formGroup.controls.monthTo.value !== null;

    this.toggleRequired(formGroup.controls.monthFrom, monthRequired);
    this.toggleRequired(formGroup.controls.monthTo, monthRequired);
    this.toggleRequired(formGroup.controls.dayFrom, dayRequired);
    this.toggleRequired(formGroup.controls.dayTo, dayRequired);

    formGroup.updateValueAndValidity({ emitEvent: false });
  }

  private static toggleRequired(
    control: AbstractControl,
    required: boolean,
  ): void {
    if (!required) {
      control.removeValidators(Validators.required);
      control.markAsPristine();
      control.markAsUntouched();
    } else {
      control.addValidators(Validators.required);
      control.markAsDirty();
    }

    control.updateValueAndValidity();
  }
}
